import { StabilityActionTypes } from './stabilityActions';

const initialState = {
  // Overall state
  loading: false,
  error: null,
  
  // System status
  systemStatus: null,
  
  // Connection information
  connectionInfo: {
    loading: false,
    error: null,
    data: null
  },
  
  // MongoDB connection details
  mongoDbDetails: {
    loading: false,
    error: null,
    data: null
  },
  
  // Environment details
  activeEnvironment: null,
  selectedDbEnvironment: null,
  selectedConnectionEnvironment: null,
  availableEnvironments: [],
  environmentDetails: {},
  connectionEnvironmentDetails: {},
  environmentLoading: {
    activeEnvironment: false,
    availableEnvironments: false,
    details: false
  },
  connectionEnvironmentLoading: {
    details: false
  },
  environmentErrors: {
    activeEnvironment: null,
    availableEnvironments: null,
    details: null
  },
  connectionEnvironmentErrors: {
    details: null
  },
  
  // Maintenance mode
  maintenanceMode: {
    enabled: false,
    message: '',
    startTime: null,
    endTime: null
  },
  
  // User limits
  userLimits: {
    maxFriends: 100,
    maxMessages: 500,
    maxWatchLater: 500,
    maxFavorites: 300,
    maxVideoHistory: 1000,
    maxQueues: 50,
    maxVideosPerQueue: 100
  },
  
  // Security settings
  security: {
    maxLoginAttempts: 5,
    loginLockoutMinutes: 15,
    passwordResetTimeoutHours: 24,
    jwtExpirationHours: 24,
    apiRateLimits: {
      enabled: true,
      requestsPerMinute: 60
    }
  },
  
  // System logs
  logs: [],
  totalLogs: 0,
  
  // UI state
  activeTab: 0,
  notificationStatus: null,
  resetStatus: null
};

const stabilityReducer = (state = initialState, action) => {
  switch (action.type) {
    // System status actions
    case StabilityActionTypes.FETCH_SYSTEM_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case StabilityActionTypes.FETCH_SYSTEM_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        systemStatus: action.payload
      };
    
    case StabilityActionTypes.FETCH_SYSTEM_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
      
    // MongoDB connection details actions
    case StabilityActionTypes.FETCH_MONGODB_DETAILS_REQUEST:
      return {
        ...state,
        mongoDbDetails: {
          ...state.mongoDbDetails,
          loading: true,
          error: null
        }
      };
    
    case StabilityActionTypes.FETCH_MONGODB_DETAILS_SUCCESS:
      return {
        ...state,
        mongoDbDetails: {
          loading: false,
          error: null,
          data: action.payload
        }
      };
    
    case StabilityActionTypes.FETCH_MONGODB_DETAILS_FAILURE:
      return {
        ...state,
        mongoDbDetails: {
          ...state.mongoDbDetails,
          loading: false,
          error: action.payload
        }
      };
    
    // Connection info actions
    case StabilityActionTypes.FETCH_CONNECTION_INFO_REQUEST:
      return {
        ...state,
        connectionInfo: {
          ...state.connectionInfo,
          loading: true,
          error: null
        }
      };
    
    case StabilityActionTypes.FETCH_CONNECTION_INFO_SUCCESS:
      return {
        ...state,
        connectionInfo: {
          loading: false,
          error: null,
          data: action.payload
        }
      };
    
    case StabilityActionTypes.FETCH_CONNECTION_INFO_FAILURE:
      return {
        ...state,
        connectionInfo: {
          ...state.connectionInfo,
          loading: false,
          error: action.payload
        }
      };
    
    // Environment actions
    case StabilityActionTypes.SET_SELECTED_DB_ENVIRONMENT:
      return {
        ...state,
        selectedDbEnvironment: action.payload
      };
    
    case StabilityActionTypes.FETCH_ACTIVE_ENVIRONMENT_REQUEST:
      return {
        ...state,
        environmentLoading: {
          ...state.environmentLoading,
          activeEnvironment: true
        },
        environmentErrors: {
          ...state.environmentErrors,
          activeEnvironment: null
        }
      };
    
    case StabilityActionTypes.FETCH_ACTIVE_ENVIRONMENT_SUCCESS:
      return {
        ...state,
        activeEnvironment: action.payload,
        environmentLoading: {
          ...state.environmentLoading,
          activeEnvironment: false
        }
      };
    
    case StabilityActionTypes.FETCH_ACTIVE_ENVIRONMENT_FAILURE:
      return {
        ...state,
        environmentLoading: {
          ...state.environmentLoading,
          activeEnvironment: false
        },
        environmentErrors: {
          ...state.environmentErrors,
          activeEnvironment: action.payload
        }
      };
    
    case StabilityActionTypes.FETCH_AVAILABLE_ENVIRONMENTS_REQUEST:
      return {
        ...state,
        environmentLoading: {
          ...state.environmentLoading,
          availableEnvironments: true
        },
        environmentErrors: {
          ...state.environmentErrors,
          availableEnvironments: null
        }
      };
    
    case StabilityActionTypes.FETCH_AVAILABLE_ENVIRONMENTS_SUCCESS:
      return {
        ...state,
        availableEnvironments: action.payload,
        environmentLoading: {
          ...state.environmentLoading,
          availableEnvironments: false
        }
      };
    
    case StabilityActionTypes.FETCH_AVAILABLE_ENVIRONMENTS_FAILURE:
      return {
        ...state,
        environmentLoading: {
          ...state.environmentLoading,
          availableEnvironments: false
        },
        environmentErrors: {
          ...state.environmentErrors,
          availableEnvironments: action.payload
        }
      };
    
    case StabilityActionTypes.FETCH_ENVIRONMENT_DETAILS_REQUEST:
      return {
        ...state,
        environmentLoading: {
          ...state.environmentLoading,
          details: true
        },
        environmentErrors: {
          ...state.environmentErrors,
          details: null
        }
      };
    
    case StabilityActionTypes.FETCH_ENVIRONMENT_DETAILS_SUCCESS:
      return {
        ...state,
        environmentDetails: {
          ...state.environmentDetails,
          [action.payload.environment]: action.payload.data
        },
        environmentLoading: {
          ...state.environmentLoading,
          details: false
        }
      };
    
    case StabilityActionTypes.FETCH_ENVIRONMENT_DETAILS_FAILURE:
      return {
        ...state,
        environmentLoading: {
          ...state.environmentLoading,
          details: false
        },
        environmentErrors: {
          ...state.environmentErrors,
          details: action.payload
        }
      };
    
    // Maintenance mode actions
    case StabilityActionTypes.TOGGLE_MAINTENANCE_MODE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case StabilityActionTypes.TOGGLE_MAINTENANCE_MODE_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        maintenanceMode: action.payload.maintenanceMode || state.maintenanceMode
      };
    
    case StabilityActionTypes.TOGGLE_MAINTENANCE_MODE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Reset application actions
    case StabilityActionTypes.RESET_APPLICATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        resetStatus: null
      };
    
    case StabilityActionTypes.RESET_APPLICATION_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        resetStatus: { success: true, message: action.payload.message }
      };
    
    case StabilityActionTypes.RESET_APPLICATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        resetStatus: { success: false, message: action.payload.message }
      };
    
    // User limits actions
    case StabilityActionTypes.UPDATE_USER_LIMITS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case StabilityActionTypes.UPDATE_USER_LIMITS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        userLimits: action.payload.userLimits || state.userLimits
      };
    
    case StabilityActionTypes.UPDATE_USER_LIMITS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // System notification actions
    case StabilityActionTypes.SEND_SYSTEM_NOTIFICATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        notificationStatus: null
      };
    
    case StabilityActionTypes.SEND_SYSTEM_NOTIFICATION_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        notificationStatus: { success: true, message: action.payload.message }
      };
    
    case StabilityActionTypes.SEND_SYSTEM_NOTIFICATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        notificationStatus: { success: false, message: action.payload.message }
      };
    
    // Broadcast notification actions
    case StabilityActionTypes.SEND_BROADCAST_NOTIFICATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        notificationStatus: null
      };
    
    case StabilityActionTypes.SEND_BROADCAST_NOTIFICATION_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        notificationStatus: { success: true, message: action.payload.message }
      };
    
    case StabilityActionTypes.SEND_BROADCAST_NOTIFICATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        notificationStatus: { success: false, message: action.payload.message }
      };
    
    // Security settings actions
    case StabilityActionTypes.UPDATE_SECURITY_SETTINGS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case StabilityActionTypes.UPDATE_SECURITY_SETTINGS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        security: action.payload.security || state.security
      };
    
    case StabilityActionTypes.UPDATE_SECURITY_SETTINGS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // System logs actions
    case StabilityActionTypes.FETCH_SYSTEM_LOGS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case StabilityActionTypes.FETCH_SYSTEM_LOGS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        logs: action.payload.logs || [],
        totalLogs: action.payload.total || 0
      };
    
    case StabilityActionTypes.FETCH_SYSTEM_LOGS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        logs: [],
        totalLogs: 0
      };
    
    // Active tab
    case StabilityActionTypes.SET_ACTIVE_STABILITY_TAB:
      return {
        ...state,
        activeTab: action.payload
      };
    
    // Connection Environment actions
    case StabilityActionTypes.SET_SELECTED_CONNECTION_ENVIRONMENT:
      return {
        ...state,
        selectedConnectionEnvironment: action.payload
      };
    
    case StabilityActionTypes.FETCH_CONNECTION_ENVIRONMENT_DETAILS_REQUEST:
      return {
        ...state,
        connectionEnvironmentLoading: {
          ...state.connectionEnvironmentLoading,
          details: true
        },
        connectionEnvironmentErrors: {
          ...state.connectionEnvironmentErrors,
          details: null
        }
      };
      case StabilityActionTypes.FETCH_CONNECTION_ENVIRONMENT_DETAILS_SUCCESS:
      console.log('Reducer: Setting connection environment details for:', action.payload.environment, action.payload.details);
      return {
        ...state,
        connectionEnvironmentDetails: {
          ...state.connectionEnvironmentDetails,
          [action.payload.environment]: action.payload.details
        },
        connectionEnvironmentLoading: {
          ...state.connectionEnvironmentLoading,
          details: false
        }
      };
    
    case StabilityActionTypes.FETCH_CONNECTION_ENVIRONMENT_DETAILS_FAILURE:
      return {
        ...state,
        connectionEnvironmentLoading: {
          ...state.connectionEnvironmentLoading,
          details: false
        },
        connectionEnvironmentErrors: {
          ...state.connectionEnvironmentErrors,
          details: action.payload.error
        }
      };
    
    default:
      return state;
  }
};

export default stabilityReducer;
